<template>
  <div class="cockpit mp-page" v-loading="loading">
    <div class="page-heading">
      <div>
        <p class="mp-eyebrow">CONTENT OPERATING SYSTEM <span class="live">· LIVE</span></p>
        <h1>{{ greeting }}</h1>
        <p class="subhead">一眼看清流水线每个环节，把要做的事交给 Agent</p>
      </div>
      <div class="heading-actions">
        <el-tag v-if="latestSyncAt || latestAsOf" type="info" effect="plain" size="large">
          数据截至 {{ formatTime(latestSyncAt || latestAsOf) }}
        </el-tag>
        <el-button :loading="loading" @click="loadAll">刷新</el-button>
        <el-button type="success" plain :loading="syncing" @click="runManualSync">
          {{ syncing ? '同步中…' : '手动同步' }}
        </el-button>
        <el-button type="primary" plain @click="$router.push('/data-analytics')">数据中心 →</el-button>
      </div>
    </div>

    <AgentCommandBar />
    <PipelineStrip />

    <el-alert
      v-if="syncFailedCount > 0"
      type="error"
      :closable="false"
      show-icon
      class="sync-alert"
    >
      <template #title>
        {{ syncFailedCount }} 个账号同步失败（部分成功 ≠ 全部成功）
        <el-button link type="primary" @click="failDialogVisible = true">查看明细</el-button>
        <el-button link type="primary" :loading="syncing" @click="retryFailed">重试失败</el-button>
      </template>
    </el-alert>

    <div class="dashboard-grid">
      <section class="mp-panel attention-card">
        <div class="mp-panel-header">
          <div>
            <div class="mp-panel-title">需关注</div>
            <p class="card-sub">异常与待办，优先处理</p>
          </div>
        </div>
        <div class="mp-panel-body">
          <div v-if="attentionItems.length" class="attention-list">
            <button
              v-for="item in attentionItems"
              :key="item.key"
              type="button"
              class="attention-row"
              :class="item.level"
              @click="item.action && item.action()"
            >
              <span class="att-dot" />
              <div>
                <strong>{{ item.title }}</strong>
                <small>{{ item.desc }}</small>
              </div>
              <label>{{ item.actionLabel }}</label>
            </button>
          </div>
          <el-empty v-else description="暂无需要处理的事项" :image-size="48" />
        </div>
      </section>

      <section class="mp-panel content-card">
        <div class="mp-panel-header">
          <div>
            <div class="mp-panel-title">内容动态</div>
            <p class="card-sub">发布队列与待审</p>
          </div>
          <el-button text type="primary" @click="$router.push('/content/library')">内容库 →</el-button>
        </div>
        <div class="mp-panel-body">
          <div class="tabs">
            <button
              v-for="t in feedTabs"
              :key="t.key"
              type="button"
              :class="{ active: feedFilter === t.key }"
              @click="feedFilter = t.key"
            >
              {{ t.label }}
              <em v-if="t.count">{{ t.count }}</em>
            </button>
          </div>
          <div v-if="filteredFeed.length" class="content-list">
            <button
              v-for="row in filteredFeed"
              :key="row.id"
              type="button"
              class="content-row"
              @click="$router.push(row.to || '/publish')"
            >
              <b class="thumb" :style="{ background: row.thumb_color || '#6b98f8' }">
                {{ row.kind === 'article' ? '✦' : '↗' }}
              </b>
              <div>
                <strong>{{ row.title }}</strong>
                <small>{{ row.subtitle }}{{ row.time ? ' · ' + formatRelative(row.time) : '' }}</small>
              </div>
              <label :class="row.badge">{{ row.badge_label }}</label>
            </button>
          </div>
          <el-empty v-else description="暂无动态" :image-size="48" />
        </div>
      </section>

      <section class="mp-panel quick-card">
        <div class="mp-panel-header">
          <div class="mp-panel-title">快速开始</div>
        </div>
        <div class="mp-panel-body">
          <div class="quick-grid">
            <button type="button" class="q-blue" @click="$router.push('/create')">
              <b>✎</b><strong>开始创作</strong><small>选择创作方式</small>
            </button>
            <button type="button" class="q-orange" @click="$router.push('/discover')">
              <b>⌕</b><strong>找选题</strong><small>热点与爆款</small>
            </button>
            <button type="button" class="q-rose" @click="$router.push('/data-analytics')">
              <b>▥</b><strong>数据中心</strong><small>深度分析</small>
            </button>
          </div>
        </div>
      </section>
    </div>


    <el-dialog v-model="failDialogVisible" title="同步失败明细" width="720px" destroy-on-close>
      <div v-for="g in failedGroups" :key="g.group_name" class="fail-group">
        <div class="fail-group-title">
          {{ g.group_name || '未分组' }}
          <el-tag type="danger" size="small" effect="plain">{{ g.count }} 失败</el-tag>
        </div>
        <el-table :data="g.accounts" size="small" stripe>
          <el-table-column prop="display_name" label="账号" width="140" />
          <el-table-column label="平台" width="90">
            <template #default="{ row }">{{ platformName(row.platform) }}</template>
          </el-table-column>
          <el-table-column prop="error" label="原因" min-width="240" show-overflow-tooltip />
        </el-table>
      </div>
      <template #footer>
        <el-button @click="failDialogVisible = false">关闭</el-button>
        <el-button type="primary" :loading="syncing" @click="retryFailed">重试失败</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { contentAnalyticsApi, publishV2Api } from '../api'
import AgentCommandBar from '../components/home/AgentCommandBar.vue'
import PipelineStrip from '../components/home/PipelineStrip.vue'

const PLATFORM_META = {
  douyin: { label: '抖音' },
  xiaohongshu: { label: '小红书' },
  bilibili: { label: 'B站' },
  zhihu: { label: '知乎' },
  toutiao: { label: '头条' },
  baijiahao: { label: '百家号' },
  kuaishou: { label: '快手' },
  weibo: { label: '微博' },
}

export default {
  name: 'Workbench',
  components: { AgentCommandBar, PipelineStrip },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const syncing = ref(false)
    const feed = ref([])
    const feedTabsRaw = ref({ all: 0, pending: 0, running: 0, done: 0 })
    const feedFilter = ref('all')
    const todoMetric = ref(null)
    const latestAsOf = ref('')
    const latestSyncAt = ref('')
    const syncFailedCount = ref(0)
    const failedGroups = ref([])
    const failDialogVisible = ref(false)
    const opsHealth = ref(null)

    const greeting = computed(() => {
      const h = new Date().getHours()
      return h < 12 ? '早上好' : h < 18 ? '下午好' : '晚上好'
    })

    const feedTabs = computed(() => [
      { key: 'all', label: '全部', count: feedTabsRaw.value.all },
      { key: 'pending', label: '待发布', count: feedTabsRaw.value.pending },
      { key: 'running', label: '发布中', count: feedTabsRaw.value.running },
      { key: 'done', label: '已完成', count: feedTabsRaw.value.done },
    ])

    const filteredFeed = computed(() => {
      if (feedFilter.value === 'all') return feed.value
      return feed.value.filter((x) => x.badge === feedFilter.value)
    })

    const attentionItems = computed(() => {
      const items = []
      if (syncFailedCount.value > 0) {
        items.push({
          key: 'sync',
          level: 'danger',
          title: `${syncFailedCount.value} 个账号同步失败`,
          desc: '部分成功 ≠ 全部成功，请重试或查看原因',
          actionLabel: '处理',
          action: () => { failDialogVisible.value = true },
        })
      }
      const todo = todoMetric.value
      if (todo && Number(todo.value) > 0) {
        items.push({
          key: 'todo',
          level: todo.warn ? 'warn' : 'info',
          title: `${todo.display} 条待处理内容`,
          desc: todo.hint || '发布队列 / 审核',
          actionLabel: '去处理',
          action: () => router.push(todo.to || '/publish'),
        })
      }
      const pending = feedTabsRaw.value.pending || 0
      if (pending > 0 && !(todo && Number(todo.value) > 0)) {
        items.push({
          key: 'pending',
          level: 'info',
          title: `${pending} 条待发布`,
          desc: '发布队列中等待执行',
          actionLabel: '查看',
          action: () => { feedFilter.value = 'pending' },
        })
      }
      const reviewCount = feed.value.filter((x) => x.badge === 'review').length
      if (reviewCount > 0) {
        items.push({
          key: 'review',
          level: 'warn',
          title: `${reviewCount} 条需人工处理`,
          desc: '失败任务或待审核内容',
          actionLabel: '查看',
          action: () => router.push('/publish'),
        })
      }
      const cookieN = Number(opsHealth.value?.needs_relogin_count || 0)
      if (cookieN > 0) {
        items.push({
          key: 'cookie',
          level: 'warn',
          title: `${cookieN} 个账号登录失效`,
          desc: '请到发布中心重新登录',
          actionLabel: '去登录',
          action: () => router.push({ path: '/publish', query: { health: 'bad' } }),
        })
      }
      const smsOff = Number(opsHealth.value?.sms?.offline_count || 0)
      if (smsOff > 0) {
        items.push({
          key: 'sms',
          level: 'warn',
          title: `${smsOff} 台短信设备离线`,
          desc: '验证码自动登录可能中断',
          actionLabel: '看设备',
          action: () => router.push('/settings/devices'),
        })
      }
      const unbound = Number(opsHealth.value?.bindings?.unbound_count || 0)
      if (unbound > 0) {
        items.push({
          key: 'bind',
          level: 'info',
          title: `${unbound} 个账号未绑手机号`,
          desc: '无法走失效自动登录',
          actionLabel: '去绑定',
          action: () => router.push('/publish'),
        })
      }
      return items.slice(0, 6)
    })

    const platformName = (code) => PLATFORM_META[code]?.label || code || '—'

    const formatRelative = (iso) => {
      if (!iso) return ''
      const t = new Date(iso).getTime()
      if (!t) return ''
      const diff = Date.now() - t
      const m = Math.floor(diff / 60000)
      if (m < 1) return '刚刚'
      if (m < 60) return `${m} 分钟前`
      const h = Math.floor(m / 60)
      if (h < 24) return `${h} 小时前`
      return `${Math.floor(h / 24)} 天前`
    }

    const formatTime = (v) => {
      if (!v) return ''
      return String(v).replace('T', ' ').slice(0, 19)
    }

    const loadSyncMeta = async () => {
      try {
        const res = await contentAnalyticsApi.getMatrixBoard({ sort: 'reads', limit: 1, offset: 0 })
        const data = res?.data || res
        latestAsOf.value = data.latest_as_of || ''
        latestSyncAt.value = data.latest_sync_at || ''
        syncFailedCount.value = data.sync?.failed_count || 0
        failedGroups.value = data.sync?.failed_groups || []
      } catch {
        /* ignore */
      }
    }

    const loadCockpit = async () => {
      const res = await contentAnalyticsApi.getCockpit({ trend_days: 7 })
      const data = res?.data || res
      feed.value = data.feed || []
      feedTabsRaw.value = data.feed_tabs || { all: 0, pending: 0, running: 0, done: 0 }
      const metrics = data.metrics || []
      todoMetric.value = metrics.find((m) => m.key === 'todo') || null
    }

    const loadOpsHealth = async () => {
      try {
        opsHealth.value = await publishV2Api.getCookieHealth()
      } catch {
        opsHealth.value = null
      }
    }

    const loadAll = async ({ quiet = false } = {}) => {
      if (!quiet) loading.value = true
      try {
        await Promise.all([loadSyncMeta(), loadCockpit(), loadOpsHealth()])
      } catch (e) {
        if (!quiet) {
          ElMessage.error('加载失败：' + (e?.response?.data?.detail || e?.message || e))
        }
      } finally {
        if (!quiet) loading.value = false
      }
    }

    const onVisibilityRefresh = () => {
      if (document.visibilityState === 'visible' && !syncing.value) {
        loadAll({ quiet: true })
      }
    }

    const pollUntilIdle = async () => {
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        const st = await publishV2Api.getDataSyncStatus()
        const state = (st?.data || st)?.state || {}
        try {
          await loadSyncMeta()
        } catch (_) { /* ignore */ }
        if (!state.running) return state.last_round || {}
      }
      return {}
    }

    const showSyncResult = async (round) => {
      const failed = round?.failed || 0
      const success = round?.success || 0
      const total = round?.total || 0
      if (failed > 0) {
        failedGroups.value = round.failed_groups || []
        syncFailedCount.value = failed
        failDialogVisible.value = true
        await ElMessageBox.alert(
          `同步结束：成功 ${success}/${total}，失败 ${failed}。部分成功不能当作全部成功。`,
          '同步部分失败',
          { type: 'warning' },
        )
      } else {
        ElMessage.success(`同步完成：成功 ${success}/${total}`)
      }
    }

    const runManualSync = async () => {
      syncing.value = true
      try {
        await publishV2Api.runDataSync({ trigger: 'manual' })
        ElMessage.info('已开始后台同步…')
        const round = await pollUntilIdle()
        await showSyncResult(round)
        await loadAll()
      } catch (e) {
        ElMessage.error(e?.response?.data?.detail || e?.message || '同步失败')
      } finally {
        syncing.value = false
      }
    }

    const retryFailed = async () => {
      syncing.value = true
      try {
        await publishV2Api.retryFailedDataSync()
        const round = await pollUntilIdle()
        await showSyncResult(round)
        await loadAll()
        failDialogVisible.value = syncFailedCount.value > 0
      } catch (e) {
        ElMessage.error(e?.response?.data?.detail || e?.message || '重试失败')
      } finally {
        syncing.value = false
      }
    }

    onMounted(() => {
      loadAll()
      document.addEventListener('visibilitychange', onVisibilityRefresh)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', onVisibilityRefresh)
    })

    return {
      loading,
      syncing,
      greeting,
      attentionItems,
      feedTabs,
      feedFilter,
      filteredFeed,
      latestAsOf,
      latestSyncAt,
      syncFailedCount,
      failedGroups,
      failDialogVisible,
      opsHealth,
      formatRelative,
      formatTime,
      platformName,
      loadAll,
      runManualSync,
      retryFailed,
    }
  },
}
</script>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.page-heading h1 {
  font-size: 27px;
  letter-spacing: -1.2px;
  margin: 0 0 7px;
  font-weight: 800;
}

.subhead {
  color: var(--mp-text-muted);
  margin: 0;
  font-size: 12px;
}

.heading-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.sync-alert { margin-bottom: 12px; }

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(240px, 0.8fr);
  gap: 18px;
}

.card-sub {
  color: var(--mp-text-muted);
  font-size: 10px;
  margin: 6px 0 0;
}

.attention-list { display: flex; flex-direction: column; gap: 2px; }

.attention-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 4px;
  border: 0;
  border-bottom: 1px solid #f1f3f6;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
}

.attention-row:last-child { border-bottom: 0; }

.att-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #91b0fa;
  flex-shrink: 0;
}

.attention-row.danger .att-dot { background: #e6855b; }
.attention-row.warn .att-dot { background: #df8d50; }

.attention-row > div { flex: 1; min-width: 0; }

.attention-row strong {
  display: block;
  font-size: 12px;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attention-row small {
  display: block;
  margin-top: 3px;
  font-size: 10px;
  color: var(--mp-text-muted);
}

.attention-row label {
  font-size: 10px;
  color: var(--mp-accent);
  white-space: nowrap;
}

.tabs {
  display: flex;
  gap: 18px;
  border-bottom: 1px solid #eef1f5;
  margin-bottom: 6px;
  color: #a0aaba;
  font-size: 11px;
}

.tabs button {
  background: transparent;
  border: 0;
  padding: 0 0 10px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  border-bottom: 2px solid transparent;
}

.tabs button.active {
  color: var(--mp-accent);
  border-bottom-color: var(--mp-accent);
  font-weight: 700;
}

.tabs em {
  font-size: 9px;
  color: var(--mp-accent);
  background: var(--mp-accent-soft);
  padding: 2px 5px;
  border-radius: 5px;
  font-style: normal;
  margin-left: 4px;
}

.content-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 0;
  border: 0;
  border-bottom: 1px solid #f1f3f6;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
}

.content-row:last-child { border-bottom: 0; }

.thumb {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: #fff;
  font-size: 12px;
  flex-shrink: 0;
}

.content-row > div { flex: 1; min-width: 0; }

.content-row strong {
  font-size: 11px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 650;
}

.content-row small {
  display: block;
  color: var(--mp-text-muted);
  font-size: 9px;
  margin-top: 4px;
}

.content-row label {
  font-size: 9px;
  padding: 4px 7px;
  border-radius: 4px;
  white-space: nowrap;
}

.done { color: #1ba477; background: #e8f7f1; }
.pending { color: #df8d50; background: #fff2e7; }
.running { color: #326cf4; background: #edf3ff; }
.review { color: #8c79d2; background: #f0edff; }

.quick-grid {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.quick-grid button {
  display: grid;
  grid-template-columns: 29px 1fr;
  grid-template-rows: auto auto;
  column-gap: 10px;
  text-align: left;
  border: 1px solid transparent;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.quick-grid button.q-blue { background: #eef4ff; }
.quick-grid button.q-orange { background: #fff4ea; }
.quick-grid button.q-rose { background: #fff0f3; }

.quick-grid button:hover { border-color: #cad8ff; }

.quick-grid b {
  grid-row: 1 / 3;
  display: grid;
  place-items: center;
  font-size: 16px;
  color: var(--mp-accent);
}

.quick-grid .q-orange b { color: #ef9a5c; }
.quick-grid .q-rose b { color: #e88aa8; }

.quick-grid strong { font-size: 12px; font-weight: 700; }
.quick-grid small { color: var(--mp-text-muted); font-size: 10px; }






.ai-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.fail-group { margin-bottom: 14px; }

.fail-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
}

@media (max-width: 1100px) {
  .dashboard-grid { grid-template-columns: 1fr; }
}

@media (max-width: 720px) {
  .page-heading { flex-direction: column; align-items: flex-start; }
  .heading-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  .ai-actions { width: 100%; }
  .ai-actions .el-button { flex: 1; }
  .quick-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
